import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import type { Profile } from '@/types';
import type { User, Session } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  isLoading: boolean;
  error: string | null;
  authListener: { data: { subscription: { unsubscribe: () => void } } } | null;

  // Actions
  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName?: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (data: Partial<Profile>) => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  session: null,
  isLoading: true,
  error: null,
  authListener: null,

  initialize: async () => {
    try {
      // Cleanup existing listener to prevent memory leak
      const existingListener = get().authListener;
      if (existingListener) {
        existingListener.data.subscription.unsubscribe();
      }

      // Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) throw sessionError;

      if (session?.user) {
        // Fetch profile
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Profile fetch error:', profileError);
        }

        set({
          user: session.user,
          session,
          profile: profile || null,
          isLoading: false
        });
      } else {
        set({ isLoading: false });
      }

      // Listen for auth changes (only register once)
      const authStateListener = supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          try {
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', session.user.id)
              .single();

            if (profileError && profileError.code !== 'PGRST116') {
              console.error('Profile fetch error:', profileError);
            }

            set({ user: session.user, session, profile: profile || null });
          } catch (error) {
            console.error('Error fetching profile on sign in:', error);
          }
        } else if (event === 'SIGNED_OUT') {
          set({ user: null, session: null, profile: null });
        }
      });

      set({ authListener: authStateListener });
    } catch (error) {
      console.error('Auth initialization error:', error);
      set({ isLoading: false, error: 'Fehler beim Laden der Sitzung' });
    }
  },

  signIn: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      if (data.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.user.id)
          .single();
        
        set({ 
          user: data.user, 
          session: data.session, 
          profile,
          isLoading: false 
        });
      }
    } catch (error: any) {
      const message = error.message === 'Invalid login credentials'
        ? 'Ungültige E-Mail oder Passwort'
        : 'Anmeldung fehlgeschlagen';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  signUp: async (email, password, displayName) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { display_name: displayName },
        },
      });
      
      if (error) throw error;
      
      // Update profile with display name if provided
      if (data.user && displayName) {
        await supabase
          .from('profiles')
          .update({ display_name: displayName })
          .eq('id', data.user.id);
      }
      
      set({ isLoading: false });
    } catch (error: any) {
      const message = error.message.includes('already registered')
        ? 'Diese E-Mail ist bereits registriert'
        : 'Registrierung fehlgeschlagen';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  signOut: async () => {
    set({ isLoading: true });
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      set({ user: null, session: null, profile: null, isLoading: false });
    } catch (error) {
      console.error('Sign out error:', error);
      set({ isLoading: false });
    }
  },

  resetPassword: async (email) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) throw error;
      set({ isLoading: false });
    } catch (error) {
      set({ error: 'Passwort-Reset fehlgeschlagen', isLoading: false });
      throw error;
    }
  },

  updateProfile: async (data) => {
    const { user } = get();
    if (!user) return;
    
    set({ isLoading: true, error: null });
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .update(data)
        .eq('id', user.id)
        .select()
        .single();
      
      if (error) throw error;
      set({ profile, isLoading: false });
    } catch (error) {
      set({ error: 'Profil-Update fehlgeschlagen', isLoading: false });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
